import { Link, useParams } from "@tanstack/solid-router";
import { For, Show, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { CsvIoButtons } from "~/components/CsvIoButtons";
import { SavedViewsBar } from "~/components/SavedViewsBar";
import { TasksBulkBar } from "~/components/TasksBulkBar";
import { useActivities, useCompleteTask, useMe, useRescheduleTask } from "~/lib/hooks";
import { useRowSelection } from "~/lib/selection";
import type { ActivityRead, ActivityStatus } from "~/lib/types";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function isOverdue(t: ActivityRead): boolean {
  return !!(t.due_date && t.status === "planned" && new Date(t.due_date) < new Date());
}

function isDueToday(t: ActivityRead): boolean {
  if (!t.due_date || t.status !== "planned") return false;
  const d = new Date(t.due_date);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function TasksPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };

  const [statusFilter, setStatusFilter] = createSignal<ActivityStatus | "">("planned");
  const [onlyMine, setOnlyMine] = createSignal(false);
  const me = useMe();

  const tasks = useActivities(
    () => params().wsKey,
    () => ({
      kind: "task",
      status: statusFilter() || undefined,
      owner_user_id: onlyMine() ? (me.data?.user_id ?? undefined) : undefined,
      limit: 100,
    }),
  );

  const completeMutation = useCompleteTask(() => params().wsKey);
  const rescheduleMutation = useRescheduleTask(() => params().wsKey);

  const snooze = (t: ActivityRead, days: number) => {
    const base = t.due_date ? new Date(t.due_date).getTime() : Date.now();
    const next = new Date(Math.max(base, Date.now()) + days * ONE_DAY_MS);
    rescheduleMutation.mutate({
      activityId: t.id,
      version: t.version,
      due_date: next.toISOString(),
    });
  };

  const overdueCount = () => (tasks.data?.items ?? []).filter(isOverdue).length;
  const dueTodayCount = () =>
    (tasks.data?.items ?? []).filter((t) => isDueToday(t) && !isOverdue(t)).length;

  const visibleRows = () => tasks.data?.items ?? [];
  const selection = useRowSelection<ActivityRead>(() => visibleRows().map((r) => r.id));

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Tasks">
      <div class="flex flex-col gap-4">
        <SavedViewsBar
          wsKey={() => params().wsKey}
          entityType={() => "task"}
          currentFilters={() => ({
            status: statusFilter() || undefined,
            only_mine: onlyMine() || undefined,
          })}
          onApply={(f) => {
            setStatusFilter((f.status as ActivityStatus | "") ?? "");
            setOnlyMine(!!f.only_mine);
          }}
          onClear={() => {
            setStatusFilter("planned");
            setOnlyMine(false);
          }}
        />
        <div class="flex gap-3 flex-wrap items-center">
          <label for="tasks-status-filter" class="text-sm opacity-70">
            Filter:
          </label>
          <select
            id="tasks-status-filter"
            class="select select-bordered select-sm"
            value={statusFilter()}
            onChange={(e) => setStatusFilter(e.currentTarget.value as ActivityStatus | "")}
          >
            <option value="planned">Open (planned)</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
          <label class="cursor-pointer label gap-2">
            <input
              type="checkbox"
              class="toggle toggle-sm"
              checked={onlyMine()}
              disabled={!me.data?.user_id}
              onChange={(e) => setOnlyMine(e.currentTarget.checked)}
            />
            <span class="label-text">Only mine</span>
          </label>
          <div class="ml-auto text-sm flex items-center gap-3">
            <Show when={overdueCount() > 0}>
              <span class="badge badge-error gap-1">{overdueCount()} overdue</span>
            </Show>
            <Show when={dueTodayCount() > 0}>
              <span class="badge badge-warning gap-1">{dueTodayCount()} due today</span>
            </Show>
            <CsvIoButtons wsKey={() => params().wsKey} plural="activities" />
          </div>
        </div>

        <Show when={tasks.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
            Loading tasks…
          </div>
        </Show>
        <Show when={tasks.isError}>
          <div role="alert" class="alert alert-error">
            Failed to load tasks.
          </div>
        </Show>

        <TasksBulkBar
          wsKey={() => params().wsKey}
          count={selection.count}
          rows={() => selection.selectedRows(visibleRows())}
          onClear={selection.clear}
        />

        <Show when={tasks.data}>
          {(data) => (
            <Show
              when={data().items.length > 0}
              fallback={<div class="text-center py-12 opacity-60">No tasks match the filter.</div>}
            >
              <div class="overflow-x-auto bg-base-100 rounded-lg shadow-sm">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th class="w-8">
                        <input
                          type="checkbox"
                          class="checkbox checkbox-sm"
                          aria-label="Select all tasks"
                          checked={
                            visibleRows().length > 0 && selection.count() === visibleRows().length
                          }
                          onChange={(e) =>
                            e.currentTarget.checked ? selection.selectAll() : selection.clear()
                          }
                        />
                      </th>
                      <th>Subject</th>
                      <th>Attached to</th>
                      <th>Status</th>
                      <th>Due</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data().items}>
                      {(t) => (
                        <TaskRow
                          wsKey={params().wsKey}
                          task={t}
                          selected={selection.has(t.id)}
                          onToggleSelect={() => selection.toggle(t.id)}
                          onComplete={() =>
                            completeMutation.mutate({
                              activityId: t.id,
                              version: t.version,
                            })
                          }
                          onReschedule={(due) =>
                            rescheduleMutation.mutate({
                              activityId: t.id,
                              version: t.version,
                              due_date: new Date(due).toISOString(),
                            })
                          }
                          onSnooze={(days) => snooze(t, days)}
                        />
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          )}
        </Show>
      </div>
    </AppShell>
  );
}

function TaskRow(props: {
  wsKey: string;
  task: ActivityRead;
  selected: boolean;
  onToggleSelect: () => void;
  onComplete: () => void;
  onReschedule: (due: string) => void;
  onSnooze: (days: number) => void;
}) {
  const [rescheduling, setRescheduling] = createSignal(false);
  const attachedTo = () =>
    props.task.primary_entity_type === "account"
      ? {
          to: "/workspaces/$wsKey/accounts/$accId",
          params: { wsKey: props.wsKey, accId: props.task.primary_entity_id },
        }
      : {
          to: "/workspaces/$wsKey/contacts/$conId",
          params: { wsKey: props.wsKey, conId: props.task.primary_entity_id },
        };

  const rowClass = () =>
    isOverdue(props.task) ? "bg-error/10" : isDueToday(props.task) ? "bg-warning/10" : "";

  return (
    <tr class={`hover ${rowClass()} ${props.selected ? "bg-base-200" : ""}`}>
      <td>
        <input
          type="checkbox"
          class="checkbox checkbox-sm"
          aria-label={`Select ${props.task.subject}`}
          checked={props.selected}
          onChange={props.onToggleSelect}
        />
      </td>
      <td class="font-medium">{props.task.subject}</td>
      <td>
        <Link {...attachedTo()} class="link link-primary text-sm">
          {props.task.primary_entity_type} · {props.task.primary_entity_id.slice(0, 12)}…
        </Link>
      </td>
      <td>
        <span
          class={`badge badge-sm ${
            props.task.status === "completed"
              ? "badge-success"
              : isOverdue(props.task)
                ? "badge-error"
                : isDueToday(props.task)
                  ? "badge-warning"
                  : "badge-outline"
          }`}
        >
          {isOverdue(props.task)
            ? "overdue"
            : isDueToday(props.task)
              ? "due today"
              : props.task.status}
        </span>
      </td>
      <td class="text-sm opacity-80">
        <Show when={rescheduling()}>
          <input
            type="datetime-local"
            class="input input-bordered input-xs"
            aria-label="New due date"
            onChange={(e) => {
              props.onReschedule(e.currentTarget.value);
              setRescheduling(false);
            }}
          />
        </Show>
        <Show when={!rescheduling()}>
          {props.task.due_date ? new Date(props.task.due_date).toLocaleDateString() : "—"}
        </Show>
      </td>
      <td class="flex gap-1 flex-wrap">
        <Show when={props.task.status !== "completed"}>
          <button
            class="btn btn-xs btn-success"
            aria-label={`Mark "${props.task.subject}" complete`}
            onClick={props.onComplete}
          >
            <span aria-hidden="true">✓</span>
          </button>
          <button
            class="btn btn-xs btn-ghost"
            title="Snooze 1 day"
            onClick={() => props.onSnooze(1)}
          >
            +1d
          </button>
          <button
            class="btn btn-xs btn-ghost"
            title="Snooze 1 week"
            onClick={() => props.onSnooze(7)}
          >
            +1w
          </button>
          <button class="btn btn-xs btn-ghost" onClick={() => setRescheduling((v) => !v)}>
            Reschedule
          </button>
        </Show>
      </td>
    </tr>
  );
}
